import { useState } from "react";
import { PrintRegularIcon, LoaderV3Icon } from "@elixir/icons";
import { ensurePagifyLoaded } from "../../../../utils/pagifySingeltonInstance";
import { ReceiptConfigManager } from "../../../../organisms/receipt-v2/receipt-config-manager";
import getB from "../../../../utils/bridges/EkaBridge";
import { returnActorNamePerformingWithPersona } from "../../../../utils/bridges/methods";
import { FirebaseAppointment } from "../../../../firestore/types";
import { createInivisbleIframe } from "../../../../utils/createInivisibleIframe";
import { ChipWithTooltip } from "ui";
import dayjs from "dayjs";
import { serializeErrorForTracking, track } from "../../../../utils/tracking";
import { caclculateAgeOfPatientFromDob } from "../../../../utils/generic-utils";
import { getMeaningOfAppointmentStatus } from "../../../../organisms/appointments-v2/appointment-status-mapping";
import { doctorNameWithSpecilisation } from "../../../../organisms/appointments-v2/get-doctor-name-with-specilisation";

const receiptConfigManager = new ReceiptConfigManager();
const OPD_SLIP_DIV = `#eka-opd-slip`;

export const usePrintOpdSlip = (apt: FirebaseAppointment) => {
  const bridge = getB();
  const receiptUtils = bridge.templar.receipt;
  const opdslipUtils = bridge.templar.opdslip;
  const allDoctors = bridge.getMe()?.docs;

  const activeDoctor = allDoctors?.find((doc) => apt?.doctor_oid == doc?.id);

  const [loading, setLoading] = useState(false);

  const handlePrintOpdSlip = async () => {
    if (loading) return;
    setLoading(true);

    track("CLICK_ON_OPD_SLIP_PRINT", {
      aid: apt.aid,
    });
    try {
      await ensurePagifyLoaded();

      let config;
      const isClilinicLevelAppointment = apt.doctor_oid?.startsWith("c-");
      if (!isClilinicLevelAppointment) {
        config = await receiptConfigManager.get({
          doctor_oid: apt?.doctor_oid,
          clinic_id: apt?.clinic_id,
        });
      }
      if (!config) {
        config = receiptUtils.getDefaultPdfConfigForReceipt();
      }

      const [patientData] =
        (await bridge.getPatientsByIds([apt?.patient_oid])) || [];

      const clinicData = bridge
        .getMe()
        .clinics.find((c) => c?.id == apt?.clinic_id);

      const patientName = [
        patientData.personal?.name?.f,
        patientData.personal?.name?.l,
      ]
        .filter(Boolean)
        .join(" ");

      const dob = patientData?.personal?.age?.dob;
      const patientAge = dob ? caclculateAgeOfPatientFromDob(dob) : undefined;

      const patientGender = patientData?.personal?.gender || undefined;
      const uhid =
        patientData?.username ||
        patientData?.formData?.find((i) => i.key == "uhid")?.value ||
        "";

      const headerHTML = opdslipUtils.getHeaderForOpdSlip({
        doctorName: doctorNameWithSpecilisation(activeDoctor!),
        headerText: activeDoctor?.professional?.signature_text || "",
        clinicName: clinicData?.name || "",
        clinicAddress: clinicData?.address?.line1 || "",
        config,
      });

      const aptData = await bridge.appointments.get(apt.aid);
      const services = aptData?.services || [];
      const paymentStatus = aptData?.payment?.status || undefined;

      const bodyHTML = opdslipUtils.getBodyForOpdSlip({
        name: patientName,
        age: patientAge,
        gender: patientGender,
        patient_mobile: [
          patientData?.personal?.phone?.c,
          patientData?.personal?.phone?.n,
        ]
          .filter(Boolean)
          .join(""),
        uhid,
        time: apt.full_date
          ? dayjs(apt.full_date as any).format("DD-MM-YYYY, hh:mmA")
          : undefined,
        doctor_name: doctorNameWithSpecilisation(activeDoctor!),
        token: apt?.token?.toString() || "",
        services: services.map(
          (s: { service_name: string; service_id: number; price: number }) => ({
            service_name: s.service_name,
            service_id: String(s.service_id),
            price: s.price,
          })
        ),
        payment_status: paymentStatus?.replace("_", " "),
      });

      const createdBy = returnActorNamePerformingWithPersona();
      const footerHTML = opdslipUtils.getFooterForOpdSlip({
        created_by: createdBy,
        created_at: dayjs().format("DD-MM-YYYY, hh:mmA"),
      });

      createInivisbleIframe(OPD_SLIP_DIV);

      const headHTML =
        receiptUtils.getPdfCssForReceipt({
          config,
        }) + opdslipUtils.getHeadCssForOpdSlip(config.page_size);

      // @ts-ignore
      await window?.pagify?.render({
        head_html: headHTML,
        isViewOnlySkipMakingPDF: true,
        containerSelector: OPD_SLIP_DIV,
        header_html: headerHTML,
        body_html: bodyHTML,
        footer_html: footerHTML,
        page_size: config?.page_size || "A4",
        header_height: `${config.heights?.header || 2.5}cm`,
        footer_height: `${(config.heights?.footer || 2) + 1}cm`,
        margin_left: `0cm`,
        margin_right: `0cm`,
        onPreviewReady: (result: { success: boolean; error?: string }) => {
          if (result.success) {
            const iframe = document?.querySelector(
              `${OPD_SLIP_DIV} iframe`
            ) as HTMLIFrameElement | null;
            if (iframe?.contentWindow) {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();
            }
          } else {
            track("ERROR", {
              on: "PRINT_OPD_SLIP",
            });
            console.error("OPD Slip preview failed:", result?.error);
          }
          setLoading(false);
        },
        onPdfError: (error: unknown) => {
          console.error("OPD Slip pagify error:", error);
          setLoading(false);
          track("ERROR", {
            on: "PRINT_OPD_SLIP_ERROR_CB",
            what: JSON.stringify(error),
          });
        },
      });
    } catch (error: any) {
      console.error("Error printing OPD slip:", error);
      track("ERROR", {
        on: "catch_block_print_opd_slip_root",
        what: serializeErrorForTracking(error),
      });
      setLoading(false);
    }
  };

  return { handlePrintOpdSlip, loading };
};

const PrintOpdSlipButton = ({ apt }: { apt: FirebaseAppointment }) => {
  const { handlePrintOpdSlip, loading } = usePrintOpdSlip(apt);

  return (
    <>
      {loading ? (
        <LoaderV3Icon
          className="dr-w-14 dr-h-14 flex-shrink-0 animate-spin"
          style={{
            fill: "none",
            stroke: "black",
          }}
        />
      ) : (
        <PrintRegularIcon
          onClick={handlePrintOpdSlip}
          className="dr-w-14 dr-h-14 flex-shrink-0"
        />
      )}
    </>
  );
};

export default PrintOpdSlipButton;
